import { useEffect, useState } from "react";
import { Loader2, Save, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";
import { Field } from "./AdminRestaurants";
import { toast } from "sonner";

export default function AdminPayments() {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/admin/tenant-settings").then(({ data }) => setCfg(data));
    }, []);

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                cod_enabled: !!cfg.cod_enabled,
                online_payment_enabled: !!cfg.online_payment_enabled,
                razorpay_mock: !!cfg.razorpay_mock,
                otp_whatsapp_enabled: !!cfg.otp_whatsapp_enabled,
                otp_sms_enabled: !!cfg.otp_sms_enabled,
            };
            if (cfg.razorpay_key_id_v2 && !cfg.razorpay_key_id_v2.startsWith("***")) {
                payload.razorpay_key_id_v2 = cfg.razorpay_key_id_v2;
            }
            if (cfg.razorpay_key_secret_v2 && !cfg.razorpay_key_secret_v2.startsWith("***")) {
                payload.razorpay_key_secret_v2 = cfg.razorpay_key_secret_v2;
            }
            await api.patch("/admin/tenant-settings", payload);
            toast.success("Payment settings saved");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

    return (
        <div className="space-y-6 max-w-2xl">
            <div>
                <h1 className="text-2xl font-bold">Payment Settings</h1>
                <p className="text-sm text-muted-foreground mt-1">Tenant-wide defaults. Each zone may override these.</p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
                <Toggle
                    testid="cod-toggle"
                    label="Cash on Delivery (COD)"
                    hint="Customers can pay with cash when the order arrives."
                    checked={!!cfg.cod_enabled}
                    onChange={(v) => setCfg({ ...cfg, cod_enabled: v })}
                />
                <Toggle
                    testid="online-toggle"
                    label="Online Payments"
                    hint="Razorpay-powered UPI / Cards / Netbanking."
                    checked={!!cfg.online_payment_enabled}
                    onChange={(v) => setCfg({ ...cfg, online_payment_enabled: v })}
                />
            </div>

            <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <ShieldCheck size={16} className="text-primary" />
                    <p className="font-bold text-sm">Razorpay credentials</p>
                </div>
                <p className="text-xs text-muted-foreground -mt-2">Leave blank to keep mocked payments. The moment both keys are filled, mock mode auto-disables.</p>
                <Field label="Key ID">
                    <input data-testid="rzp-key-id" className="inp" value={cfg.razorpay_key_id_v2 || ""} onChange={(e) => setCfg({ ...cfg, razorpay_key_id_v2: e.target.value })} placeholder="rzp_live_xxx or rzp_test_xxx" />
                </Field>
                <Field label="Key Secret">
                    <input data-testid="rzp-key-secret" className="inp" type="password" value={cfg.razorpay_key_secret_v2 || ""} onChange={(e) => setCfg({ ...cfg, razorpay_key_secret_v2: e.target.value })} placeholder="Encrypted at rest" />
                </Field>
                <Toggle
                    testid="rzp-mock"
                    label="Mock mode"
                    hint="When ON, online payments are auto-captured for testing."
                    checked={!!cfg.razorpay_mock}
                    onChange={(v) => setCfg({ ...cfg, razorpay_mock: v })}
                />
            </div>

            <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
                <div>
                    <p className="font-bold text-sm">Login OTP Channels</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Pick which channels customers can use to receive their login OTP. At least one must stay enabled.</p>
                </div>
                <Toggle
                    testid="otp-whatsapp-toggle"
                    label="WhatsApp OTP"
                    hint="Cheaper, higher delivery rate. Sent via Pingbix template."
                    checked={cfg.otp_whatsapp_enabled !== false}
                    onChange={(v) => setCfg({ ...cfg, otp_whatsapp_enabled: v })}
                />
                <Toggle
                    testid="otp-sms-toggle"
                    label="SMS OTP"
                    hint="Fallback for users without WhatsApp. SMS costs more per send."
                    checked={cfg.otp_sms_enabled !== false}
                    onChange={(v) => setCfg({ ...cfg, otp_sms_enabled: v })}
                />
            </div>

            <button data-testid="payments-save" disabled={busy} onClick={save}
                className="bg-primary text-primary-foreground rounded-xl px-5 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
            </button>
        </div>
    );
}

function Toggle({ label, hint, checked, onChange, testid }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div>
                <p className="font-bold text-sm">{label}</p>
                {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
            </div>
            <button data-testid={testid} onClick={() => onChange(!checked)}
                className={`relative inline-flex w-12 h-7 rounded-full transition ${checked ? "bg-primary" : "bg-muted"}`}>
                <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition ${checked ? "translate-x-5" : ""}`} />
            </button>
        </div>
    );
}
